// The workspace's brands, loaded once and shared — and which one is being
// looked at.
//
// Two surfaces read this at the same time: the switcher in the top bar and the
// Connections screen. They have to agree, or renaming a brand on one leaves the
// other showing the old name until a navigation, and filtering in the header
// does nothing visible. So the list and the choice both live in a module-level
// store with listeners, the same shape `useConnections` uses and for the same
// reason.
//
// The choice is a view filter and never authority: it decides what is on screen
// and nothing else. Every query underneath it is still scoped by RLS to the
// workspace.

import { useCallback, useEffect, useState } from "react";
import { listBrands, rememberBrand, rememberedBrandId, type Brand } from "./brands";

const cache = new Map<string, Brand[]>();
// `true` when the list itself has to be re-read. Choosing a brand is a view
// change and must not cost a round trip, so it announces with `false`.
const listeners = new Set<(reload: boolean) => void>();
// Workspace id -> the brand being looked at. Null, or absent, is "all brands".
const chosen = new Map<string, string | null>();

function announce(reload: boolean) {
  for (const notify of listeners) notify(reload);
}

/** Drop the cached list so the next reader re-reads it. Call after creating,
 *  renaming or deleting a brand. */
export function forgetBrands(workspaceId?: string) {
  if (workspaceId) cache.delete(workspaceId);
  else cache.clear();
  announce(true);
}

export interface BrandsState {
  brands: Brand[];
  /** False until the first answer — cached or fresh — has arrived. */
  ready: boolean;
  error: string | null;
  /** Null = all brands. */
  activeBrandId: string | null;
  /** The row for `activeBrandId`, or null when showing everything. */
  activeBrand: Brand | null;
  selectBrand: (brandId: string | null) => void;
  reload: () => Promise<void>;
}

export function useBrands(workspaceId: string | null): BrandsState {
  const [brands, setBrands] = useState<Brand[]>(
    () => (workspaceId ? cache.get(workspaceId) ?? [] : []),
  );
  const [ready, setReady] = useState(() => !!workspaceId && cache.has(workspaceId));
  const [error, setError] = useState<string | null>(null);
  const [, bump] = useState(0);

  const load = useCallback(async (force: boolean) => {
    if (!workspaceId) {
      setBrands([]);
      setReady(false);
      return;
    }
    const hit = cache.get(workspaceId);
    if (hit && !force) {
      setBrands(hit);
      setReady(true);
      return;
    }
    try {
      const rows = await listBrands(workspaceId);
      cache.set(workspaceId, rows);
      setBrands(rows);
      setError(null);
    } catch (e: any) {
      // A brand list that failed to load must not empty the account list: the
      // switcher simply does not appear, and every account stays reachable.
      setError(e?.message ?? String(e));
    } finally {
      setReady(true);
    }
  }, [workspaceId]);

  useEffect(() => { void load(false); }, [load]);

  useEffect(() => {
    const notify = (reload: boolean) => {
      bump((n) => n + 1);
      if (reload) void load(true);
    };
    listeners.add(notify);
    return () => { listeners.delete(notify); };
  }, [load]);

  // The remembered choice is read once per workspace and then kept in memory,
  // so every reader gets the same answer without touching storage on a render.
  if (workspaceId && !chosen.has(workspaceId)) {
    chosen.set(workspaceId, rememberedBrandId(workspaceId));
  }
  const stored = workspaceId ? chosen.get(workspaceId) ?? null : null;

  // A brand that has since been deleted, or belongs to a workspace this person
  // has left, resolves to "all" rather than to an empty screen with a filter
  // nobody can see.
  const activeBrand = stored ? brands.find((b) => b.id === stored) ?? null : null;
  const activeBrandId = activeBrand?.id ?? null;

  const selectBrand = useCallback((brandId: string | null) => {
    if (!workspaceId) return;
    chosen.set(workspaceId, brandId);
    rememberBrand(workspaceId, brandId);
    announce(false);
  }, [workspaceId]);

  return {
    brands, ready, error, activeBrandId, activeBrand, selectBrand,
    reload: () => load(true),
  };
}
